import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useDebouncedEffect } from "@wojtekmaj/react-hooks";

import {
  formFor,
  OpenGroupRequestSchema,
  ourFormFallbackLanguage,
  parsePublishedInstrumentSchema,
  SaveFormDraftRequestSchema,
  StartFormDraftRequestSchema,
  supportedByOurForm,
  type Assessment,
  type AssessmentId,
  type FormDraft,
  type FormElement,
  type InstrumentDigest,
  type OpenGroupRequest,
  type PreviousCommentsAnswer,
  type PublishedInstrumentSchema,
  type SaveFormDraftRequest,
  type StartFormDraftRequest,
  type SubmissionDocumentUnbranded,
} from "@thalamos/haven-common";

import type { FormDraftResponse } from "../../api/formDraft/api.js";
import api, { apiHooks } from "../../api/index.js";

// TODO(assessments): let the respondent choose the language.
/** The language our form is shown in. */
export const FORM_LANGUAGE = "en";

/**
 * How long answering must pause before the draft is saved: long enough that a
 * run of answers is one save rather than one each, short enough that little is
 * at risk if the connection drops. Leaving the assessment saves at once either
 * way.
 */
const SAVE_AFTER_MS = 1000;

/** An assessment's form, opened on its draft: what a screen needs to show it. */
export type OpenedForm = {
  schema: PublishedInstrumentSchema;
  form: FormElement[];
  draft: FormDraft;
  /** Last time's comments, where the draft carried ratings over and the reader may read them; absent otherwise. */
  previous?: NonNullable<PreviousCommentsAnswer["previous"]> | undefined;
  /** Whether carrying ratings over was asked for as this draft started and refused, so it started empty. */
  carryOverRefused?: boolean | undefined;
};

/** Opening the form: loading, open, or failed for a reason worth telling apart. */
export type Opening =
  | { kind: "loading" }
  | { kind: "open"; opened: OpenedForm }
  | { kind: "failed"; reason: "not_found" | "not_open" | "unavailable" };

/** Whether the latest answers have reached the server. */
export type SaveState = "saved" | "saving" | "unsaved" | "failed";

/** How one save went. */
export type SaveOutcome = Extract<SaveState, "saved" | "failed">;

/** An instrument version's schema and the form elements our form builds from it. */
export type InstrumentForm = Pick<OpenedForm, "schema" | "form">;

/**
 * The calls an assessment's form is read, opened and saved through, all of
 * them for the one assessment the screens have open: the staff form's routes
 * for a practitioner, and the guest session's for a guest.
 */
export type FormClient = {
  /** The pinned version's schema, unparsed, for `parsePublishedInstrumentSchema` to judge. */
  instrumentSchema: (
    digest: InstrumentDigest,
  ) => Promise<{ status: number; data: unknown }>;
  formDraft: {
    create: (
      assessmentId: AssessmentId,
      start?: StartFormDraftRequest,
    ) => Promise<FormDraftResponse<FormDraft>>;
    resume: (
      assessmentId: AssessmentId,
    ) => Promise<FormDraftResponse<FormDraft>>;
    save: (
      assessmentId: AssessmentId,
      body: SaveFormDraftRequest,
    ) => Promise<FormDraftResponse<FormDraft>>;
    previousComments: (
      assessmentId: AssessmentId,
    ) => Promise<FormDraftResponse<PreviousCommentsAnswer>>;
    openGroup: (
      assessmentId: AssessmentId,
      body: OpenGroupRequest,
    ) => Promise<FormDraftResponse<undefined>>;
  };
};

/** The staff form's routes, read from the API client when called rather than when imported. */
const staffFormClient = (): FormClient => ({
  instrumentSchema: api.instrument.schema,
  formDraft: api.formDraft,
});

/** Fetches an instrument version's schema through the routes given and builds our form from it. */
export const buildForm = async ({
  name,
  digest,
  instrumentSchema,
}: {
  name: string;
  digest: InstrumentDigest;
  instrumentSchema: FormClient["instrumentSchema"];
}): Promise<InstrumentForm | "unavailable"> => {
  const fetched = await instrumentSchema(digest);
  const schema =
    fetched.status === 200
      ? parsePublishedInstrumentSchema(fetched.data)
      : undefined;

  if (schema === undefined || schema.isErr()) {
    return "unavailable";
  }

  const built = formFor({
    supported: supportedByOurForm,
    name,
    schema: schema.value,
    language: FORM_LANGUAGE,
    fallbackLanguage: ourFormFallbackLanguage,
  });

  return built.isErr()
    ? "unavailable"
    : { schema: schema.value, form: built.value.form };
};

/** Fetches an instrument version's schema through the staff routes and builds our form from it. */
export const loadForm = (
  name: string,
  digest: InstrumentDigest,
): Promise<InstrumentForm | "unavailable"> =>
  buildForm({ name, digest, instrumentSchema: api.instrument.schema });

/** Loading an instrument version's form: loading, ready, or failed. */
export type FormLoading =
  | { kind: "loading" }
  | { kind: "ready"; loaded: InstrumentForm }
  | { kind: "failed" };

/**
 * Builds the form of the instrument version an assessment pinned, whatever
 * state the assessment is in, with no draft; `retry` loads it again.
 */
export const useInstrumentForm = (
  assessment: Assessment | undefined,
): { loading: FormLoading; retry: () => void } => {
  const [loading, setLoading] = useState<FormLoading>({ kind: "loading" });
  const [attempt, setAttempt] = useState(0);
  // Keyed on the strings the form is built from rather than the instrument
  // object, so the form loads again only when one of them changes.
  const name = assessment?.instrument.name;
  const digest = assessment?.instrument.digest;

  useEffect(() => {
    if (name === undefined || digest === undefined) {
      return;
    }

    let current = true;

    void loadForm(name, digest)
      .catch((): "unavailable" => "unavailable")
      .then((loaded) => {
        if (current) {
          setLoading(
            loaded === "unavailable"
              ? { kind: "failed" }
              : { kind: "ready", loaded },
          );
        }
      });

    return () => {
      current = false;
    };
  }, [name, digest, attempt]);

  const retry = useCallback(() => {
    setLoading({ kind: "loading" });
    setAttempt((count) => count + 1);
  }, []);

  return { loading, retry };
};

/**
 * Opens an assessment's form on its draft: the pinned schema, the form
 * elements built from it, and the draft as last saved, started where there is
 * none.
 */
const useOpening = ({
  assessment,
  carryOverAsked,
  client,
}: {
  assessment: Assessment | undefined;
  carryOverAsked: boolean;
  client: FormClient;
}): Opening => {
  const [opening, setOpening] = useState<Opening>({ kind: "loading" });
  const id = assessment?.id;
  // Strings rather than the instrument object, which every re-read replaces,
  // so the form is opened again only when what it is built from changes.
  const name = assessment?.instrument.name;
  const digest = assessment?.instrument.digest;
  const open = assessment?.status === "open";

  useEffect(() => {
    if (id === undefined || name === undefined || digest === undefined) {
      return;
    }

    if (!open) {
      setOpening({ kind: "failed", reason: "not_open" });
      return;
    }

    // Reset, or an assessment reopened on screen keeps showing its `not_open`
    // refusal until the draft loads.
    setOpening({ kind: "loading" });
    let current = true;

    const load = async (): Promise<Opening> => {
      const loaded = await buildForm({
        name,
        digest,
        instrumentSchema: client.instrumentSchema,
      });

      if (loaded === "unavailable") {
        return { kind: "failed", reason: "unavailable" };
      }

      const { formDraft } = client;
      const resumed = await formDraft.resume(id);
      // A start that asked to carry ratings over made the draft before coming
      // here, unless that failed on the way; this asks again, and starts empty
      // where the server now refuses.
      let carryOverRefused = false;
      const startDraft = async () => {
        if (!carryOverAsked) {
          return formDraft.create(id);
        }

        const carried = await formDraft.create(
          id,
          StartFormDraftRequestSchema.parse({ carryOver: true }),
        );
        carryOverRefused = carried.status === 400;

        return carryOverRefused ? formDraft.create(id) : carried;
      };
      const created = resumed.status === 404 ? await startDraft() : resumed;
      // The draft was started since the resume, perhaps by this assessment's
      // own earlier opening, so carry on with it.
      const draft =
        created.status === 409 ? await formDraft.resume(id) : created;

      if (draft.data === undefined) {
        return { kind: "failed", reason: "unavailable" };
      }

      // Only for reference, so failing to read them opens the form without.
      const previous =
        draft.data.carriedRatings === null
          ? undefined
          : await formDraft
              .previousComments(id)
              .then((answer) => answer.data?.previous ?? undefined)
              .catch(() => undefined);

      return {
        kind: "open",
        opened: { ...loaded, draft: draft.data, previous, carryOverRefused },
      };
    };

    void load()
      .catch((): Opening => ({ kind: "failed", reason: "unavailable" }))
      .then((loaded) => {
        if (current) {
          setOpening(loaded);
        }
      });

    return () => {
      current = false;
    };
  }, [id, name, digest, open, carryOverAsked, client]);

  return opening;
};

/**
 * Saves the draft once answering pauses, and whatever is unsaved when the
 * assessment is left; closing or reloading the tab with answers unsaved asks
 * first.
 *
 * One save at a time, in the order answered, because the server keeps the last
 * save to arrive and an older document must never land after a newer one. Only
 * the latest save says whether the answers are saved, but any save that lands
 * keeps every answer it carries.
 *
 * `assessmentId` is undefined until the assessment is first read, before which
 * no answer can change.
 */
const useDraftSaving = (
  assessmentId: AssessmentId | undefined,
  saveDraft: FormClient["formDraft"]["save"],
) => {
  const [save, setSave] = useState<SaveState>("saved");
  // What the server returned for the latest save: the context it took, so a
  // sitting that crosses midnight judges answers against the new day; when it
  // was kept; and who saved each answer.
  const [saved, setSaved] =
    useState<Pick<FormDraft, "context" | "savedAt" | "answerStamps">>();
  // The answers as of the latest change, which a screen opened after it reads
  // in place of the draft as first opened.
  const [answers, setAnswers] = useState<SubmissionDocumentUnbranded>();
  const [answered, setAnswered] = useState(0);
  // Each named answer not yet kept, with the revision of its latest change, so
  // a screen opened after the change still knows.
  const [unsaved, setUnsaved] = useState<ReadonlyMap<string, number>>(
    new Map(),
  );
  const unsavedAnswers = useMemo(() => new Set(unsaved.keys()), [unsaved]);
  // Counts every change, so a save knows which changes its document carries.
  const revision = useRef(0);
  const pending = useRef<{
    document: SubmissionDocumentUnbranded;
    revision: number;
  }>(undefined);
  const queue = useRef<Promise<SaveOutcome>>(Promise.resolve("saved"));
  const latest = useRef(0);

  // Settles once every save asked for so far has, with how the last went.
  const flush = useCallback((): Promise<SaveOutcome> => {
    const carried = pending.current;

    if (carried === undefined) {
      return queue.current;
    }

    // Unreachable: answers change only in an open form, so the assessment has
    // been read and `assessmentId` comes from that cached read. Kept to fail
    // loudly if not.
    /* v8 ignore if */
    if (assessmentId === undefined) {
      throw new Error(
        "Expected answers to change only once the assessment is read",
      );
    }

    const request = SaveFormDraftRequestSchema.safeParse({
      document: carried.document,
    });

    pending.current = undefined;
    latest.current += 1;
    const sequence = latest.current;
    setSave("saving");

    const send = async (): Promise<SaveOutcome> => {
      // The server would refuse it, so it fails as a refused save does.
      // Unreachable while the form builds the document from a parsed draft,
      // adding only option values and comment strings.
      /* v8 ignore if */
      if (!request.success) {
        return "failed";
      }

      const response = await saveDraft(assessmentId, request.data);

      if (response.data === undefined) {
        return "failed";
      }

      setSaved({
        context: response.data.context,
        savedAt: response.data.savedAt,
        answerStamps: response.data.answerStamps,
      });
      return "saved";
    };

    queue.current = queue.current.then(() =>
      send()
        .catch((): SaveOutcome => "failed")
        .then((outcome) => {
          // A failed document stays pending, so leaving the assessment tries
          // it again, unless a newer one has followed it: each save carries
          // the whole document, and an older one must never land after it.
          if (
            outcome === "failed" &&
            sequence === latest.current &&
            pending.current === undefined
          ) {
            pending.current = carried;
          }

          // Each save carries the whole document, so one that lands keeps
          // every change up to its own, however later saves go.
          if (outcome === "saved") {
            setUnsaved(
              (was) =>
                new Map([...was].filter(([, at]) => at > carried.revision)),
            );
          }

          if (
            sequence === latest.current &&
            (outcome === "failed" || pending.current === undefined)
          ) {
            setSave(outcome);
          }

          return outcome;
        }),
    );

    return queue.current;
  }, [assessmentId, saveDraft]);

  useDebouncedEffect(
    () => {
      void flush();
    },
    [answered],
    SAVE_AFTER_MS,
  );

  // Leaving within the pause would otherwise drop the last answers.
  useEffect(
    () => () => {
      void flush();
    },
    [flush],
  );

  useEffect(() => {
    if (save === "saved") {
      return;
    }

    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };

    window.addEventListener("beforeunload", warn);
    return () => {
      window.removeEventListener("beforeunload", warn);
    };
  }, [save]);

  const onDocumentChange = useCallback(
    (document: SubmissionDocumentUnbranded, changed?: string) => {
      revision.current += 1;
      const at = revision.current;
      pending.current = { document, revision: at };
      if (changed !== undefined) {
        setUnsaved((was) => new Map(was).set(changed, at));
      }
      setAnswers(document);
      setSave("unsaved");
      setAnswered((count) => count + 1);
    },
    [],
  );

  return {
    save,
    saved,
    answers,
    unsaved: unsavedAnswers,
    onDocumentChange,
    flushNow: flush,
  };
};

/** One assessment as every screen inside it shares it. */
export type AssessmentContextValue = {
  /** The assessment as last read, or undefined until read or once withheld. */
  assessment: Assessment | undefined;
  opening: Opening;
  save: SaveState;
  /** The answers, by the key each change named, whose latest change no save has yet kept. */
  unsavedAnswers: ReadonlySet<string>;
  /**
   * Takes the document as the latest answers, saved once answering pauses.
   * `changed` names the answer altered, to be reported in `unsavedAnswers`
   * until a save that carries this change lands.
   */
  onDocumentChange: (
    document: SubmissionDocumentUnbranded,
    changed?: string,
  ) => void;
  /**
   * Saves the latest answers now rather than once answering pauses, settling
   * when every save asked for so far has, with how the last of them went.
   */
  flushNow: () => Promise<SaveOutcome>;
  /**
   * Records that the assessor has opened one of the draft's groups, which the
   * draft then reports among its `openedGroups` once the server has kept it.
   */
  onGroupOpened: (group: string) => Promise<void>;
  /**
   * Reads the assessment again, for a change this page made, rather than wait
   * on a realtime event a deployment may not deliver. Rejects where the read
   * does not answer, leaving the assessment as last read.
   */
  reread: () => Promise<void>;
};

const AssessmentContext = createContext<AssessmentContextValue | undefined>(
  undefined,
);

/**
 * Where reading the assessment stands: under way, read, withheld from this
 * session (the server's answer for an assessment it may no longer see, which
 * must take away what was shown), or failed for another reason.
 */
export type AssessmentRead =
  | { kind: "reading" }
  | { kind: "read"; assessment: Assessment }
  | { kind: "withheld" }
  | { kind: "failed" };

/**
 * Reads one assessment through the staff routes and opens its form on one
 * draft with one save queue, for every screen inside it to share.
 *
 * Key it by the assessment, so nothing one assessment holds is shown, or
 * saved, under another's address.
 */
export const AssessmentProvider = ({
  requestedId,
  carryOverAsked = false,
  children,
}: {
  /** The id the address names, which the assessment is read by. */
  requestedId: string;
  /** Whether the start that led here asked to carry the earlier ratings over. */
  carryOverAsked?: boolean;
  children: ReactNode;
}) => {
  const [{ data, response, error }, refetch] =
    apiHooks.assessment.byId(requestedId);
  const reread = useCallback(async () => {
    await refetch();
  }, [refetch]);
  const [client] = useState(staffFormClient);
  // A withheld read answers 404; a 403 is a refusal all the same.
  const read: AssessmentRead =
    response?.status === 200 && data !== undefined
      ? { kind: "read", assessment: data }
      : response?.status === 404 || response?.status === 403
        ? { kind: "withheld" }
        : (error !== undefined && error !== null) || response !== undefined
          ? { kind: "failed" }
          : { kind: "reading" };

  return (
    <AssessmentFormProvider
      read={read}
      reread={reread}
      client={client}
      carryOverAsked={carryOverAsked}
    >
      {children}
    </AssessmentFormProvider>
  );
};

/**
 * Opens an assessment read elsewhere on one draft with one save queue,
 * through the client given, for every screen inside it to share.
 *
 * `client` must keep its identity across renders: the form opens again
 * whenever it changes.
 */
export const AssessmentFormProvider = ({
  read: latestRead,
  reread,
  client,
  carryOverAsked = false,
  children,
}: {
  read: AssessmentRead;
  /** Reads the assessment again; rejects where the read does not answer. */
  reread: () => Promise<void>;
  client: FormClient;
  /** Whether the start that led here asked to carry the earlier ratings over. */
  carryOverAsked?: boolean;
  children: ReactNode;
}) => {
  const read = latestRead.kind === "read" ? latestRead.assessment : undefined;
  const withheld = latestRead.kind === "withheld";

  // The assessment as last read, so a re-read that fails does not take the
  // form, and the answers in it, away from the assessor.
  const [cached, setCached] = useState<Assessment>();
  useEffect(() => {
    if (read !== undefined) {
      setCached(read);
    }
  }, [read]);
  const assessment = withheld ? undefined : cached;

  // From the assessment as last read, which outlives its withholding, so
  // answers left unsaved as it is withheld are still sent.
  const assessmentId = cached?.id;
  const opening = useOpening({ assessment, carryOverAsked, client });
  const { save, saved, answers, unsaved, onDocumentChange, flushNow } =
    useDraftSaving(assessmentId, client.formDraft.save);
  const failedToRead = withheld
    ? "not_found"
    : assessment === undefined && latestRead.kind === "failed"
      ? "unavailable"
      : undefined;

  // The groups opened in this sitting, which the draft as first opened does not
  // yet know of.
  const [openedHere, setOpenedHere] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const onGroupOpened = useCallback(
    async (group: string) => {
      // Unreachable: groups open only in an open form, so the assessment has
      // been read and `assessmentId` comes from that cached read. Kept to fail
      // loudly if not.
      /* v8 ignore if */
      if (assessmentId === undefined) {
        throw new Error(
          "Expected a group to open only once the assessment is read",
        );
      }

      const request = OpenGroupRequestSchema.safeParse({ group });
      // A failure leaves the group not opened, so the next visit to one of
      // its items tries again.
      const recorded = request.success
        ? await client.formDraft
            .openGroup(assessmentId, request.data)
            .catch(() => undefined)
        : // Unreachable: the group comes from the pinned instrument, and
          // publishing refuses a group key that fails the same `isIdentifier`.
          /* v8 ignore next */
          undefined;

      if (recorded?.status === 204) {
        setOpenedHere((was) => new Set(was).add(group));
      }
    },
    [assessmentId, client],
  );

  const shown = useMemo((): Opening => {
    if (failedToRead !== undefined) {
      return { kind: "failed", reason: failedToRead };
    }

    if (opening.kind !== "open") {
      return opening;
    }

    const { draft } = opening.opened;
    const openedGroups = [
      ...new Set([...draft.openedGroups, ...openedHere]),
    ].sort();

    // Every save follows a change, so there are answers whenever there is a save.
    return {
      kind: "open",
      opened: {
        ...opening.opened,
        draft:
          answers === undefined
            ? { ...draft, openedGroups }
            : { ...draft, ...saved, document: answers, openedGroups },
      },
    };
  }, [failedToRead, opening, saved, answers, openedHere]);

  // Memoised so the screens inside re-render only when something they read
  // changes.
  const value = useMemo(
    (): AssessmentContextValue => ({
      assessment,
      opening: shown,
      save,
      unsavedAnswers: unsaved,
      onDocumentChange,
      flushNow,
      onGroupOpened,
      reread,
    }),
    [
      assessment,
      shown,
      save,
      unsaved,
      onDocumentChange,
      flushNow,
      onGroupOpened,
      reread,
    ],
  );

  return (
    <AssessmentContext.Provider value={value}>
      {children}
    </AssessmentContext.Provider>
  );
};

/** The assessment the surrounding `AssessmentProvider` has open. */
export const useAssessment = (): AssessmentContextValue => {
  const context = useContext(AssessmentContext);
  if (context === undefined) {
    throw new Error("useAssessment must be used within an AssessmentProvider");
  }
  return context;
};
